/*
 * ReportsDashboard — iter320 + iter321
 * ---------------------------------------------------------------------------
 * iter321 incremental additions (DO NOT change existing behavior):
 *   - Property Name / Drive / Remarks multi-select filters
 *   - Search box inside every filter dropdown + Select All / Clear
 *   - Global search bar (hotel / owner / city / state)
 *   - OTA "N/A" → "Pending" (display-only)
 *   - Drive "Done" click → opens property's Google Drive link in new tab
 *   - Manual remark edit (persists via PUT /api/reports/dashboard/remark/{id})
 *   - Week / Custom / Monthly all send resolved start/end so backend no
 *     longer 400s. `date_mode=all` behavior preserved exactly.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Plus, RefreshCw, X, Check, Search, Pencil, Settings2, Eye, EyeOff, ChevronUp, ChevronDown, Lock, Download,
  Building2, FileText, ClipboardList, Home, UserCircle2, Landmark, HardDrive, MessageSquare, Zap, Globe } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { usePermissions } from '../context/PermissionContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const EXTRA_OTAS_KEY = 'reports_dashboard_extra_otas';
// iter344 — persist every active filter across page refreshes. Filters
// only clear when the user clicks "Reset filters" (or removes them
// individually). The payload is a single JSON blob so adding a new
// filter later only requires one key.
const FILTERS_LS_KEY = 'reports_dashboard_filters_v1';

const _loadPersistedFilters = () => {
  try {
    const raw = localStorage.getItem(FILTERS_LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) { return {}; }
};
const _asArr = (v) => (Array.isArray(v) ? v.filter(x => typeof x === 'string') : []);
const _asStr = (v) => (typeof v === 'string' ? v : '');

const toneToTextClass = (tone) => {
  switch (tone) {
    case 'green': return 'text-emerald-600';
    case 'amber': return 'text-amber-500';
    case 'red':   return 'text-red-500';
    case 'teal':  return 'text-teal-600';
    case 'gray':
    default:      return 'text-gray-700';
  }
};

// iter293 — Reference-matching palette (soft pastel bg + icon chip).
// Keyed by card.key — OTA cards (`ota::<name>`) fall back to a palette
// slot based on their position in the dashboard OTA column list.
const _OTA_PALETTE = [
  { cardBg: 'bg-rose-50',    iconBg: 'bg-white',  iconColor: 'text-rose-500' },
  { cardBg: 'bg-amber-50',   iconBg: 'bg-white',  iconColor: 'text-amber-500' },
  { cardBg: 'bg-red-50',     iconBg: 'bg-white',  iconColor: 'text-red-500' },
  { cardBg: 'bg-yellow-50',  iconBg: 'bg-white',  iconColor: 'text-yellow-600' },
  { cardBg: 'bg-blue-50',    iconBg: 'bg-blue-600', iconColor: 'text-white' },
  { cardBg: 'bg-pink-50',    iconBg: 'bg-white',  iconColor: 'text-pink-500' },
  { cardBg: 'bg-orange-50',  iconBg: 'bg-orange-500', iconColor: 'text-white' },
  { cardBg: 'bg-purple-50',  iconBg: 'bg-purple-600', iconColor: 'text-white' },
  { cardBg: 'bg-emerald-50', iconBg: 'bg-emerald-500', iconColor: 'text-white' },
  { cardBg: 'bg-cyan-50',    iconBg: 'bg-white',  iconColor: 'text-cyan-600' },
];
const getCardStyle = (key, otaIndex = 0) => {
  const map = {
    all:          { cardBg: 'bg-sky-50',     iconBg: 'bg-sky-100',     iconColor: 'text-sky-600',     Icon: Building2 },
    documents:    { cardBg: 'bg-emerald-50', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', Icon: FileText },
    master_form:  { cardBg: 'bg-purple-50',  iconBg: 'bg-purple-100',  iconColor: 'text-purple-600',  Icon: ClipboardList },
    property:     { cardBg: 'bg-amber-50',   iconBg: 'bg-amber-100',   iconColor: 'text-amber-600',   Icon: Home },
    account:      { cardBg: 'bg-teal-50',    iconBg: 'bg-teal-100',    iconColor: 'text-teal-600',    Icon: UserCircle2 },
    banking:      { cardBg: 'bg-indigo-50',  iconBg: 'bg-indigo-100',  iconColor: 'text-indigo-600',  Icon: Landmark },
    drive:        { cardBg: 'bg-lime-50',    iconBg: 'bg-lime-100',    iconColor: 'text-lime-700',    Icon: HardDrive },
    remarks:      { cardBg: 'bg-rose-50',    iconBg: 'bg-rose-100',    iconColor: 'text-rose-600',    Icon: MessageSquare },
    ota_status:   { cardBg: 'bg-cyan-50',    iconBg: 'bg-cyan-100',    iconColor: 'text-cyan-600',    Icon: Zap },
  };
  if (map[key]) return map[key];
  const p = _OTA_PALETTE[otaIndex % _OTA_PALETTE.length];
  return { ...p, Icon: Globe };
};

const Badge = ({ tone, children, testid }) => {
  const map = {
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    grey: 'bg-gray-100 text-gray-500 border-gray-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${map[tone] || map.grey}`} data-testid={testid}>
      {children}
    </span>
  );
};

// Searchable multi-select with Select All / Clear All. Preserves current
// selections while typing so users can build multi-value filters.
const SearchableMultiSelect = ({ label, options, value, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const toggle = (v) => onChange(value.includes(v) ? value.filter(x => x !== v) : [...value, v]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(o => String(o).toLowerCase().includes(needle));
  }, [q, options]);

  const selectAllVisible = () => {
    const merged = Array.from(new Set([...value, ...filtered]));
    onChange(merged);
  };

  useEffect(() => {
    // Close dropdown on outside click.
    if (!open) return;
    const handler = (e) => {
      const el = document.querySelector(`[data-testid="${testid}"]`);
      if (el && !el.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, testid]);

  return (
    <div className="relative" data-testid={testid}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs bg-white hover:bg-gray-50 flex items-center gap-1"
        data-testid={`${testid}-button`}>
        {label}{value.length > 0 && <span className="bg-teal-100 text-teal-700 px-1.5 rounded-full text-[10px]">{value.length}</span>}
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="flex items-center justify-between p-2 border-b border-gray-100">
            <span className="text-[11px] font-bold text-gray-500 uppercase">{label}</span>
            <div className="flex items-center gap-2">
              <button onClick={selectAllVisible} className="text-[10px] text-teal-600 hover:underline" data-testid={`${testid}-select-all`}>Select All</button>
              {value.length > 0 && (
                <button onClick={() => onChange([])} className="text-[10px] text-teal-600 hover:underline" data-testid={`${testid}-clear`}>Clear</button>
              )}
            </div>
          </div>
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded border border-gray-200">
              <Search size={11} className="text-gray-400"/>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={`Search ${label.toLowerCase()}...`}
                className="flex-1 bg-transparent outline-none text-xs"
                data-testid={`${testid}-search`}
              />
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 && <div className="p-2 text-xs text-gray-400">No options</div>}
            {filtered.map(opt => (
              <label key={opt} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 cursor-pointer text-xs">
                <input type="checkbox" checked={value.includes(opt)} onChange={() => toggle(opt)} data-testid={`${testid}-opt-${opt}`}/>
                <span className="truncate">{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Interactive Masterform cell for the Reports Dashboard.
// Every state (Pending / Correction / Complete) is clickable → the same
// popover with all three options so the user can freely switch.
//
// Admin extras:
//  - A Lock / Unlock toggle is always visible inside the popover.
//  - Once locked, non-admin users see a lock icon and cannot open the
//    popover; the backend also rejects their PUT with 403.
//  - When an admin edits the status to anything other than "Complete",
//    the backend auto-clears the lock.
const MasterFormCell = ({ row, onSaved, onLockChanged, isAdmin }) => {
  const status = row?.master_form?.status || 'Pending';
  const locked = !!row?.master_form?.locked;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const applyStatus = async (next) => {
    if (next === status) { setOpen(false); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/api/reports/dashboard/master-form-status/${row.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ status: next }),
      });
      if (r.status === 403) throw new Error('Masterform is locked by admin');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json().catch(() => ({}));
      // Backend may auto-clear the lock when moving off Complete.
      onSaved(row.id, next, typeof body.locked === 'boolean' ? body.locked : locked);
      toast.success(`Masterform set to ${next}`);
      setOpen(false);
    } catch (e) {
      toast.error(e.message || 'Failed to update Masterform');
    } finally {
      setSaving(false);
    }
  };

  const applyLock = async (nextLocked) => {
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/api/reports/dashboard/master-form-lock/${row.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ locked: nextLocked }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      onLockChanged(row.id, nextLocked);
      toast.success(nextLocked ? 'Masterform locked' : 'Masterform unlocked');
      setOpen(false);
    } catch (_) {
      toast.error('Failed to toggle lock');
    } finally {
      setSaving(false);
    }
  };

  const toneFor = (s) => (s === 'Complete' ? 'green' : s === 'Correction' ? 'amber' : 'red');

  // Non-admin + locked → read-only badge with a lock icon tooltip.
  if (locked && !isAdmin) {
    return (
      <span className="inline-flex items-center gap-1" data-testid={`dashboard-mform-locked-${row.id}`}>
        <Badge tone={toneFor(status)} testid={`dashboard-mform-${row.id}`}>{status}</Badge>
        <Lock size={11} className="text-gray-400" title="Locked by admin" />
      </span>
    );
  }

  // Everyone else → clickable popover with all three options (and the
  // admin-only Lock/Unlock row).
  const options = ['Pending', 'Correction', 'Complete'];
  const dotFor = (s) => s === 'Complete' ? 'bg-emerald-500' : s === 'Correction' ? 'bg-amber-500' : 'bg-red-500';
  const hoverFor = (s) => s === 'Complete' ? 'hover:bg-emerald-50' : s === 'Correction' ? 'hover:bg-amber-50' : 'hover:bg-red-50';

  return (
    <span ref={anchorRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="focus:outline-none cursor-pointer inline-flex items-center gap-1"
        disabled={saving}
        data-testid={`dashboard-mform-btn-${row.id}`}
        title="Click to change Masterform status"
      >
        <Badge tone={toneFor(status)} testid={`dashboard-mform-${row.id}`}>{status}</Badge>
        {locked && isAdmin && <Lock size={11} className="text-gray-500" title="Locked (admin)" />}
      </button>
      {open && (
        <div
          className="absolute z-50 mt-1 left-0 w-40 bg-white border border-gray-200 rounded-lg shadow-xl py-1"
          data-testid={`dashboard-mform-popover-${row.id}`}
        >
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => applyStatus(opt)}
              disabled={saving}
              className={`w-full text-left px-3 py-1.5 text-xs text-gray-700 ${hoverFor(opt)} flex items-center gap-2 disabled:opacity-50 ${status === opt ? 'font-semibold bg-gray-50' : ''}`}
              data-testid={`dashboard-mform-opt-${opt.toLowerCase()}-${row.id}`}
            >
              <span className={`inline-block w-2 h-2 rounded-full ${dotFor(opt)}`}></span>
              {opt}
              {status === opt && <Check size={11} className="ml-auto text-gray-500" />}
            </button>
          ))}
          {isAdmin && (
            <>
              <div className="my-1 border-t border-gray-100" />
              <button
                type="button"
                onClick={() => applyLock(!locked)}
                disabled={saving}
                className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                data-testid={`dashboard-mform-lock-toggle-${row.id}`}
              >
                <Lock size={11} className={locked ? 'text-emerald-600' : 'text-gray-500'} />
                {locked ? 'Unlock (admin)' : 'Lock (admin)'}
              </button>
            </>
          )}
        </div>
      )}
    </span>
  );
};

const RemarkEditor = ({ row, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(row.manual_remark || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(row.manual_remark || ''); }, [row.manual_remark]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_BASE}/api/reports/dashboard/remark/${row.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ remark: value.trim() }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      onSaved(row.id, value.trim());
      toast.success('Remark saved');
      setOpen(false);
    } catch (e) {
      toast.error('Failed to save remark');
    } finally { setSaving(false); }
  };

  return (
    <div className="inline-flex items-start gap-1">
      <div className="flex flex-col gap-0.5">
        {row.remarks === 'Active' && <Badge tone="green">Active</Badge>}
        {row.remarks === 'Inactive' && <Badge tone="grey">Inactive</Badge>}
        {row.remarks && !['Active', 'Inactive'].includes(row.remarks) && <Badge tone="blue">{row.remarks}</Badge>}
        {!row.remarks && !row.manual_remark && <span className="text-gray-300">—</span>}
        {row.manual_remark && (
          <span className="text-[11px] text-gray-600 italic max-w-[180px] truncate" title={row.manual_remark}>
            {row.manual_remark}
          </span>
        )}
      </div>
      <button
        onClick={() => setOpen(true)}
        className="text-gray-400 hover:text-teal-600 mt-0.5"
        title="Edit remark"
        data-testid={`dashboard-remark-edit-${row.id}`}>
        <Pencil size={11}/>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()} data-testid={`dashboard-remark-modal-${row.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-bold text-gray-800 text-sm">Edit Remark</h3>
                <p className="text-xs text-gray-500 mt-0.5">{row.hotel_name}</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={16}/></button>
            </div>
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              rows={3}
              placeholder="e.g. OTA mapping issue — Agoda link pending"
              maxLength={500}
              className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              data-testid={`dashboard-remark-input-${row.id}`}
            />
            <div className="mt-3 flex items-center justify-between text-[11px] text-gray-400">
              <span>{value.length}/500</span>
              <span>Property Status ({row.remarks || '—'}) is not affected.</span>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => { setValue(''); }} className="px-3 py-1.5 text-xs text-gray-600 hover:text-red-600" data-testid={`dashboard-remark-clear-${row.id}`}>Clear</button>
              <button onClick={save} disabled={saving} className="px-3 py-1.5 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-700 disabled:opacity-60" data-testid={`dashboard-remark-save-${row.id}`}>
                {saving ? <Loader2 className="animate-spin inline" size={12}/> : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ManageHeadersModal = ({ open, onClose, columns, onSaved, cards, onCardsSaved, onCardsReset }) => {
  const [local, setLocal] = useState([]);
  const [localCards, setLocalCards] = useState([]);  // iter292 — card show/order
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLocal(columns.map(c => ({ ...c })));
      setLocalCards(((cards) || []).map(c => ({ ...c })));
    }
  }, [open, columns, cards]);

  const toggle = (key) => setLocal(l => l.map(c => c.key === key ? { ...c, enabled: !c.enabled } : c));
  const move = (idx, dir) => setLocal(l => {
    const arr = [...l];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return arr;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return arr;
  });
  const restoreAll = () => setLocal(l => l.map(c => ({ ...c, enabled: true })));
  const hideAll = () => setLocal(l => l.map(c => ({ ...c, enabled: false })));

  // iter292 — Card-specific handlers (symmetric with columns above).
  const toggleCard = (key) => setLocalCards(l => l.map(c => c.key === key ? { ...c, enabled: !c.enabled } : c));
  const moveCard = (idx, dir) => setLocalCards(l => {
    const arr = [...l];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return arr;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return arr;
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        columns: local.map((c, i) => ({
          key: c.key,
          enabled: c.enabled,
          display_order: i + 1,
        })),
      };
      const r = await fetch(`${API_BASE}/api/reports/dashboard/columns`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        if (r.status === 403) throw new Error('Only admins can update column visibility');
        throw new Error(`HTTP ${r.status}`);
      }
      const body = await r.json();
      onSaved(body.columns || []);
      // iter292 — Persist card config alongside the column save.
      if (onCardsSaved) onCardsSaved(localCards);
      toast.success('Dashboard columns updated');
      onClose();
    } catch (e) {
      toast.error(e.message || 'Failed to save columns');
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="dashboard-manage-headers-modal">
        <div className="flex items-start justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-800 text-base">Manage Dashboard Headers</h3>
            <p className="text-xs text-gray-500 mt-0.5">Toggle visibility and reorder columns for every user.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="dashboard-manage-headers-close"><X size={18}/></button>
        </div>
        <div className="flex items-center justify-between px-5 py-2 border-b border-gray-100 bg-gray-50">
          <span className="text-[11px] text-gray-500">{local.filter(c => c.enabled).length} of {local.length} enabled</span>
          <div className="flex items-center gap-3">
            <button onClick={hideAll} className="text-[11px] text-gray-500 hover:text-red-600" data-testid="dashboard-manage-headers-hide-all">Hide All</button>
            <button onClick={restoreAll} className="text-[11px] text-teal-600 hover:underline" data-testid="dashboard-manage-headers-restore-all">Restore All</button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
          {/* iter292 — Summary Cards section (top). Toggles + reorder for
              the Dashboard Summary Cards. Doesn't touch the column list. */}
          {localCards.length > 0 && (
            <div className="bg-teal-50/40">
              <div className="px-5 pt-3 pb-2 flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wide text-teal-700">Summary Cards</span>
                <button
                  onClick={() => { if (onCardsReset) onCardsReset(); onClose(); }}
                  className="text-[11px] text-teal-700 hover:underline"
                  data-testid="dashboard-manage-cards-reset"
                >Reset Cards</button>
              </div>
              {localCards.map((c, idx) => (
                <div key={c.key} className="flex items-center justify-between px-5 py-2 hover:bg-teal-50" data-testid={`dashboard-manage-card-row-${c.key}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex flex-col">
                      <button disabled={idx === 0} onClick={() => moveCard(idx, -1)} className="text-gray-400 hover:text-teal-600 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`dashboard-manage-card-up-${c.key}`} title="Move up"><ChevronUp size={12}/></button>
                      <button disabled={idx === localCards.length - 1} onClick={() => moveCard(idx, 1)} className="text-gray-400 hover:text-teal-600 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`dashboard-manage-card-down-${c.key}`} title="Move down"><ChevronDown size={12}/></button>
                    </div>
                    <span className="text-xs text-gray-400 w-6 text-right tabular-nums">{idx + 1}.</span>
                    <span className="text-sm text-gray-800 truncate">{c.label}</span>
                    <span className="text-[10px] uppercase tracking-wide text-teal-600 bg-teal-100 px-1.5 py-0.5 rounded">Card</span>
                  </div>
                  <button
                    onClick={() => toggleCard(c.key)}
                    className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${c.enabled ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'}`}
                    data-testid={`dashboard-manage-card-toggle-${c.key}`}
                  >
                    {c.enabled ? <><Eye size={11}/> Enabled</> : <><EyeOff size={11}/> Hidden</>}
                  </button>
                </div>
              ))}
            </div>
          )}
          {local.map((c, idx) => (
            <div key={c.key} className="flex items-center justify-between px-5 py-2 hover:bg-gray-50" data-testid={`dashboard-manage-header-row-${c.key}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex flex-col">
                  <button
                    disabled={idx === 0}
                    onClick={() => move(idx, -1)}
                    className="text-gray-400 hover:text-teal-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`dashboard-manage-header-up-${c.key}`}
                    title="Move up">
                    <ChevronUp size={12}/>
                  </button>
                  <button
                    disabled={idx === local.length - 1}
                    onClick={() => move(idx, 1)}
                    className="text-gray-400 hover:text-teal-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`dashboard-manage-header-down-${c.key}`}
                    title="Move down">
                    <ChevronDown size={12}/>
                  </button>
                </div>
                <span className="text-xs text-gray-400 w-6 text-right tabular-nums">{idx + 1}.</span>
                <span className="text-sm text-gray-800 truncate">{c.label}</span>
                {c.is_ota && <span className="text-[10px] uppercase tracking-wide text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded">OTA</span>}
              </div>
              <button
                onClick={() => toggle(c.key)}
                className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border ${c.enabled ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'}`}
                data-testid={`dashboard-manage-header-toggle-${c.key}`}>
                {c.enabled ? <><Eye size={11}/> Enabled</> : <><EyeOff size={11}/> Hidden</>}
              </button>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800" data-testid="dashboard-manage-headers-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="px-3 py-1.5 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-700 disabled:opacity-60" data-testid="dashboard-manage-headers-save">
            {saving ? <Loader2 className="animate-spin inline" size={12}/> : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ReportsDashboard = () => {
  // iter344 — hydrate filter state from localStorage so a refresh keeps
  // every active filter (date mode, multi-selects, global search). The
  // persisted payload is written below in a dedicated useEffect and
  // cleared on resetFilters().
  const _persisted = useMemo(_loadPersistedFilters, []);
  const [dateMode, setDateMode] = useState(() => _asStr(_persisted.dateMode) || 'all');
  const [startDate, setStartDate] = useState(() => _asStr(_persisted.startDate));
  const [endDate, setEndDate] = useState(() => _asStr(_persisted.endDate));
  const [monthPick, setMonthPick] = useState(() => _asStr(_persisted.monthPick));
  const [states, setStates] = useState(() => _asArr(_persisted.states));
  const [cities, setCities] = useState(() => _asArr(_persisted.cities));
  const [owners, setOwners] = useState(() => _asArr(_persisted.owners));
  const [propertyStatuses, setPropertyStatuses] = useState(() => _asArr(_persisted.propertyStatuses));
  const [docStatuses, setDocStatuses] = useState(() => _asArr(_persisted.docStatuses));
  const [masterFormStatuses, setMasterFormStatuses] = useState(() => _asArr(_persisted.masterFormStatuses));
  const [propertyNames, setPropertyNames] = useState(() => _asArr(_persisted.propertyNames));
  const [drives, setDrives] = useState(() => _asArr(_persisted.drives));
  const [remarksFilter, setRemarksFilter] = useState(() => _asArr(_persisted.remarksFilter));
  // iter285 — Reports Dashboard OTA + OTA Status filters.
  const [otasFilter, setOtasFilter] = useState(() => _asArr(_persisted.otasFilter));
  const [otaStatusFilter, setOtaStatusFilter] = useState(() => _asArr(_persisted.otaStatusFilter));
  const [globalSearch, setGlobalSearch] = useState(() => _asStr(_persisted.globalSearch));
  const [extraOtas, setExtraOtas] = useState(() => {
    try { return JSON.parse(localStorage.getItem(EXTRA_OTAS_KEY) || '[]'); }
    catch (_) { return []; }
  });
  const [otaOptions, setOtaOptions] = useState({ ota_names: [], default_columns: [] });
  const [addOtaOpen, setAddOtaOpen] = useState(false);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pendingModal, setPendingModal] = useState(null);
  const [columnsConfig, setColumnsConfig] = useState([]); // iter322 — admin-managed column visibility + order
  const [manageOpen, setManageOpen] = useState(false);
  const { isAdmin } = usePermissions();

  useEffect(() => {
    localStorage.setItem(EXTRA_OTAS_KEY, JSON.stringify(extraOtas));
  }, [extraOtas]);

  // iter344 — persist every active filter to localStorage on change.
  // Hydration happens in the useState initializers above; this effect
  // only writes. resetFilters() clears the entry explicitly.
  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_LS_KEY, JSON.stringify({
        dateMode, startDate, endDate, monthPick,
        states, cities, owners, propertyStatuses,
        docStatuses, masterFormStatuses, propertyNames,
        drives, remarksFilter, otasFilter, otaStatusFilter,
        globalSearch,
      }));
    } catch (_) { /* ignore quota */ }
  }, [dateMode, startDate, endDate, monthPick, states, cities, owners,
      propertyStatuses, docStatuses, masterFormStatuses, propertyNames,
      drives, remarksFilter, otasFilter, otaStatusFilter, globalSearch]);

  // iter322 — load persisted column config so all users see the same
  // visibility/order the admin configured.
  const fetchColumnsConfig = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/reports/dashboard/columns`, { headers: authHeader() });
      if (r.ok) {
        const b = await r.json();
        setColumnsConfig(b.columns || []);
      }
    } catch (_) { /* ignore */ }
  }, []);
  useEffect(() => { fetchColumnsConfig(); }, [fetchColumnsConfig]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/reports/dashboard/ota-options`, { headers: authHeader() });
        if (r.ok) setOtaOptions(await r.json());
      } catch (_) { /* ignore */ }
    })();
  }, []);

  // Resolve week / monthly / custom → concrete start/end (frontend authoritative).
  const effectiveRange = useMemo(() => {
    if (dateMode === 'week') {
      const now = new Date();
      const day = now.getDay();
      const monday = new Date(now); monday.setDate(now.getDate() - ((day + 6) % 7));
      const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
      return { start: monday.toISOString().slice(0, 10), end: sunday.toISOString().slice(0, 10) };
    }
    if (dateMode === 'monthly') {
      const src = monthPick ? new Date(monthPick + '-01') : new Date();
      const y = src.getFullYear(); const m = src.getMonth();
      const start = new Date(y, m, 1); const end = new Date(y, m + 1, 0);
      return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
    }
    if (dateMode === 'custom') return { start: startDate || null, end: endDate || null };
    return { start: null, end: null };
  }, [dateMode, startDate, endDate, monthPick]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set('date_mode', dateMode);
      if (effectiveRange.start) p.set('start_date', effectiveRange.start);
      if (effectiveRange.end) p.set('end_date', effectiveRange.end);
      if (states.length) p.set('states', states.join('|'));
      if (cities.length) p.set('cities', cities.join('|'));
      if (owners.length) p.set('owners', owners.join('|'));
      if (propertyStatuses.length) p.set('property_statuses', propertyStatuses.join('|'));
      if (docStatuses.length) p.set('doc_statuses', docStatuses.join('|'));
      if (masterFormStatuses.length) p.set('master_form_statuses', masterFormStatuses.join('|'));
      if (propertyNames.length) p.set('property_names', propertyNames.join('|'));
      if (drives.length) p.set('drives', drives.join('|'));
      if (remarksFilter.length) p.set('remarks', remarksFilter.join('|'));
      if (otasFilter.length) p.set('otas', otasFilter.join('|'));
      if (otaStatusFilter.length) p.set('ota_statuses', otaStatusFilter.join('|'));
      if (extraOtas.length) p.set('extra_otas', extraOtas.join('|'));
      if (globalSearch.trim()) p.set('q', globalSearch.trim());
      p.set('page', String(page));
      p.set('limit', '25');
      const r = await fetch(`${API_BASE}/api/reports/dashboard?${p.toString()}`, { headers: authHeader() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      toast.error('Dashboard load failed');
    } finally { setLoading(false); }
  }, [dateMode, effectiveRange, states, cities, owners, propertyStatuses, docStatuses,
      masterFormStatuses, propertyNames, drives, remarksFilter, otasFilter, otaStatusFilter, extraOtas, globalSearch, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // iter292 — Dashboard Summary Cards: fetch the *entire* filtered dataset
  // (same filter payload as the paginated table, limit=10000) so cards
  // and table are guaranteed to be derived from identical rows. The
  // paginated table fetch above is untouched.
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const fetchSummary = useCallback(async () => {
    try {
      setSummaryLoading(true);
      const p = new URLSearchParams();
      p.set('date_mode', dateMode);
      if (effectiveRange.start) p.set('start_date', effectiveRange.start);
      if (effectiveRange.end) p.set('end_date', effectiveRange.end);
      if (states.length) p.set('states', states.join('|'));
      if (cities.length) p.set('cities', cities.join('|'));
      if (owners.length) p.set('owners', owners.join('|'));
      if (propertyStatuses.length) p.set('property_statuses', propertyStatuses.join('|'));
      if (docStatuses.length) p.set('doc_statuses', docStatuses.join('|'));
      if (masterFormStatuses.length) p.set('master_form_statuses', masterFormStatuses.join('|'));
      if (propertyNames.length) p.set('property_names', propertyNames.join('|'));
      if (drives.length) p.set('drives', drives.join('|'));
      if (remarksFilter.length) p.set('remarks', remarksFilter.join('|'));
      if (otasFilter.length) p.set('otas', otasFilter.join('|'));
      if (otaStatusFilter.length) p.set('ota_statuses', otaStatusFilter.join('|'));
      if (extraOtas.length) p.set('extra_otas', extraOtas.join('|'));
      if (globalSearch.trim()) p.set('q', globalSearch.trim());
      p.set('page', '1');
      p.set('limit', '10000');
      const r = await fetch(`${API_BASE}/api/reports/dashboard?${p.toString()}`, { headers: authHeader() });
      if (!r.ok) return;
      const body = await r.json();
      setSummaryRows(body?.data || []);
    } catch {
      /* summary is best-effort; cards show '-' when the fetch fails */
    } finally { setSummaryLoading(false); }
  }, [dateMode, effectiveRange, states, cities, owners, propertyStatuses, docStatuses,
      masterFormStatuses, propertyNames, drives, remarksFilter, otasFilter, otaStatusFilter,
      extraOtas, globalSearch]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // Reset page whenever any filter or search changes.
  useEffect(() => { setPage(1); }, [states, cities, owners, propertyStatuses, docStatuses,
    masterFormStatuses, propertyNames, drives, remarksFilter, otasFilter, otaStatusFilter, globalSearch, dateMode,
    startDate, endDate, monthPick]);

  const resetFilters = () => {
    setStates([]); setCities([]); setOwners([]); setPropertyStatuses([]);
    setDocStatuses([]); setMasterFormStatuses([]);
    setPropertyNames([]); setDrives([]); setRemarksFilter([]);
    setOtasFilter([]); setOtaStatusFilter([]);
    setGlobalSearch('');
    setDateMode('all'); setStartDate(''); setEndDate(''); setMonthPick('');
    setPage(1);
    // iter344 — explicit manual reset also wipes the persisted blob so
    // the next refresh truly starts from a clean slate.
    try { localStorage.removeItem(FILTERS_LS_KEY); } catch (_) { /* ignore */ }
  };

  // iter288 — See below (moved under `visibleColumns`) for the Export to
  // Excel handler. It must be declared AFTER `visibleColumns` because its
  // dependency array references that memoized value.
  const [exporting, setExporting] = useState(false);

  const addOta = (name) => {
    if (!extraOtas.includes(name)) setExtraOtas([...extraOtas, name]);
    setAddOtaOpen(false);
  };
  const removeOtaColumn = (name) => setExtraOtas(extraOtas.filter(x => x !== name));

  const otaCols = useMemo(() => data?.ota_columns || [], [data]);
  const rows = data?.data || [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  const availableOtaAdds = otaOptions.ota_names
    .filter(n => !otaCols.some(c => c.toLowerCase() === n.toLowerCase()))
    .filter(n => !extraOtas.includes(n));

  const fmtDate = (v) => v ? String(v).slice(0, 10) : '—';

  // iter322 — Build the effective column list: use persisted config order/enabled,
  // and always include any OTA columns currently rendered by the Dashboard data
  // (so newly + Add-OTA'd columns show up before the backend config refresh).
  const otaSlug = (name) => {
    const s = String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    return s ? `ota_${s}` : 'ota_unknown';
  };
  const mergedColumns = useMemo(() => {
    const cfg = columnsConfig.length ? columnsConfig : [];
    const known = new Map(cfg.map(c => [c.key, c]));
    // Ensure every currently-rendered OTA has an entry (default enabled).
    otaCols.forEach(name => {
      const k = otaSlug(name);
      if (!known.has(k)) {
        known.set(k, {
          key: k, label: name, is_ota: true, enabled: true,
          display_order: known.size + 1,
        });
      }
    });
    return Array.from(known.values()).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  }, [columnsConfig, otaCols]);

  const visibleColumns = useMemo(() => mergedColumns.filter(c => c.enabled), [mergedColumns]);

  // ================== iter292/293 — Dashboard Summary Cards ==================
  // All card counts are derived from `summaryRows` (same filtered dataset
  // as the table). No separate backend logic — guaranteed to stay in sync.
  // iter293 — The single aggregated OTA card is replaced by one card per
  // OTA platform (`ota::<name>`). Each platform card gets its own toggle
  // + reorder entry in Manage Headers.
  const DEFAULT_CARDS = useMemo(() => {
    const base = [
      { key: 'all',          label: 'All Properties',  enabled: true },
      { key: 'documents',    label: 'Documents',       enabled: true },
      { key: 'master_form',  label: 'Master Form',     enabled: true },
      { key: 'property',     label: 'Property Status', enabled: true },
      { key: 'account',      label: 'Account',         enabled: true },
      { key: 'banking',      label: 'Banking',         enabled: true },
      { key: 'drive',        label: 'Drive',           enabled: true },
      { key: 'remarks',      label: 'Remarks',         enabled: true },
      { key: 'ota_status',   label: 'OTA Status',      enabled: true },
    ];
    const otaCols = (data?.ota_columns) || [];
    const otaCards = otaCols.map(c => ({ key: `ota::${c}`, label: c, enabled: true }));
    return [...base, ...otaCards];
  }, [data?.ota_columns]);
  const CARDS_LS_KEY = 'dashboard-summary-cards-config-v2';
  const [cardConfig, setCardConfig] = useState(() => {
    try {
      const raw = localStorage.getItem(CARDS_LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch { /* fall through */ }
    return null;  // populated by the merge effect once DEFAULT_CARDS resolves
  });
  // Merge newly-appearing default keys (e.g. extra OTA columns the admin
  // just added) into the persisted config without losing user order.
  useEffect(() => {
    setCardConfig(prev => {
      if (!DEFAULT_CARDS.length) return prev;
      if (!prev) return DEFAULT_CARDS;
      const seen = new Set(prev.map(c => c.key));
      const extras = DEFAULT_CARDS.filter(c => !seen.has(c.key));
      if (!extras.length) {
        // Also refresh labels in case an OTA display name changed.
        return prev.map(c => {
          const d = DEFAULT_CARDS.find(x => x.key === c.key);
          return d ? { ...c, label: d.label } : c;
        });
      }
      return [...prev, ...extras];
    });
  }, [DEFAULT_CARDS]);
  const saveCardConfig = useCallback((next) => {
    setCardConfig(next);
    try { localStorage.setItem(CARDS_LS_KEY, JSON.stringify(next)); } catch { /* ignore quota */ }
  }, []);
  const resetCardConfig = useCallback(() => {
    try { localStorage.removeItem(CARDS_LS_KEY); } catch { /* ignore */ }
    setCardConfig(DEFAULT_CARDS);
  }, [DEFAULT_CARDS]);

  // Per-card counts computed from the shared filtered rows. Each entry is
  // `{title, items: [{label, value, tone}], layout}` so the renderer stays
  // simple. `layout` is 'bignum' | 'list'; OTA cards use 'single'.
  const cardData = useMemo(() => {
    const rows = summaryRows || [];
    const map = {};
    map.all = { title: 'All Properties', layout: 'single', items: [
      { label: 'Total Properties', value: rows.length, tone: 'teal' },
    ]};
    let docC = 0, docP = 0;
    rows.forEach(r => { ((r.documents || {}).status === 'Complete') ? docC++ : docP++; });
    map.documents = { title: 'Documents', layout: 'bignum', items: [
      { label: 'Complete', value: docC, tone: 'green' },
      { label: 'Pending',  value: docP, tone: 'amber' },
    ]};
    let mfC = 0, mfP = 0, mfX = 0;
    rows.forEach(r => {
      const s = ((r.master_form || {}).status || '').toLowerCase();
      if (s === 'complete') mfC++;
      else if (s === 'correction') mfX++;
      else mfP++;
    });
    map.master_form = { title: 'Master Form', layout: 'bignum', items: [
      { label: 'Complete',   value: mfC, tone: 'green' },
      { label: 'Pending',    value: mfP, tone: 'amber' },
      { label: 'Correction', value: mfX, tone: 'red' },
    ]};
    const psBuckets = {};
    rows.forEach(r => {
      const v = (r.property_status || 'Unknown').trim() || 'Unknown';
      psBuckets[v] = (psBuckets[v] || 0) + 1;
    });
    const psOrder = ['Active', 'Inactive', 'Terminated (Handed Owner)', 'Not Started', 'Unknown', 'Only Listing', 'Owner Left'];
    const psDotTone = (k) => {
      if (k === 'Active') return 'green';
      if (k === 'Inactive') return 'gray';
      if (k.startsWith('Terminated')) return 'red';
      if (k === 'Not Started') return 'amber';
      if (k === 'Only Listing') return 'teal';
      if (k === 'Owner Left') return 'red';
      return 'gray';
    };
    map.property = { title: 'Property Status', layout: 'list', items:
      [...psOrder.filter(k => psBuckets[k]),
       ...Object.keys(psBuckets).filter(k => !psOrder.includes(k))]
        .map(k => ({
          label: k.startsWith('Terminated') ? 'Terminated' : k,
          value: psBuckets[k], tone: psDotTone(k),
        })),
    };
    const ownerBuckets = {};
    rows.forEach(r => {
      const v = (r.closed_won_owner || 'Unassigned').trim() || 'Unassigned';
      ownerBuckets[v] = (ownerBuckets[v] || 0) + 1;
    });
    map.account = { title: 'Account', subtitle: '(Closed Won Owner)', layout: 'list', items:
      Object.entries(ownerBuckets)
        .sort((a, b) => b[1] - a[1])
        .map(([label, value]) => ({ label, value, tone: 'teal' })),
    };
    let bankC = 0, bankP = 0;
    rows.forEach(r => {
      const pending = (r.documents || {}).pending || [];
      pending.includes('Bank Details') ? bankP++ : bankC++;
    });
    map.banking = { title: 'Banking', layout: 'bignum', items: [
      { label: 'Complete', value: bankC, tone: 'green' },
      { label: 'Pending',  value: bankP, tone: 'amber' },
    ]};
    const drBuckets = {};
    rows.forEach(r => {
      const v = r.drive || 'Pending';
      drBuckets[v] = (drBuckets[v] || 0) + 1;
    });
    const drOrder = ['Done', 'Pending'];
    map.drive = { title: 'Drive', layout: 'bignum', items:
      [...drOrder.filter(k => drBuckets[k]),
       ...Object.keys(drBuckets).filter(k => !drOrder.includes(k))]
        .map(k => ({ label: k, value: drBuckets[k], tone: k === 'Done' ? 'green' : 'amber' })),
    };
    const rmBuckets = {};
    rows.forEach(r => {
      const v = r.remarks || 'Active';
      rmBuckets[v] = (rmBuckets[v] || 0) + 1;
    });
    const rmOrder = ['Active', 'Inactive'];
    const rmDotTone = (k) => (k === 'Active' ? 'green' : k === 'Inactive' ? 'gray' : k.startsWith('Terminated') ? 'red' : k === 'Owner Left' ? 'red' : k === 'Only Listing' ? 'teal' : 'amber');
    map.remarks = { title: 'Remarks', layout: 'list', items:
      [...rmOrder.filter(k => rmBuckets[k]),
       ...Object.keys(rmBuckets).filter(k => !rmOrder.includes(k))]
        .map(k => ({ label: k.startsWith('Terminated') ? 'Terminated' : k, value: rmBuckets[k], tone: rmDotTone(k) })),
    };
    const osAllBuckets = ['Live', 'Self Manage', 'Integration', 'Pending'];
    const osActiveBuckets = (otaStatusFilter && otaStatusFilter.length) ? otaStatusFilter : osAllBuckets;
    const osBuckets = Object.fromEntries(osActiveBuckets.map(k => [k, 0]));
    // iter297 — Aggregate from `ota_records` (ALL platforms the lead is
    // actually on, not just the 6 dashboard columns). Every record is
    // one data point — fully OTA-wise, no dedupe.
    const allOtaCols = (data?.ota_columns) || [];
    const otasFilterSet = (otasFilter && otasFilter.length) ? new Set(otasFilter) : null;
    // Expand aliases so "MMT" filter matches both "MMT" and "MakeMyTrip"
    // records on the raw ota_name field.
    const aliasesPerCol = {
      'MMT': ['MMT', 'MakeMyTrip'],
      'Booking.com': ['Booking.com', 'Booking_Com', 'BookingCom'],
    };
    const aliasOf = (c) => (aliasesPerCol[c] || [c]);
    const otasFilterAliasSet = otasFilterSet
      ? new Set([...otasFilterSet].flatMap(c => aliasOf(c).map(x => x.toLowerCase())))
      : null;
    rows.forEach(r => {
      (r.ota_records || []).forEach(rec => {
        if (!rec || !rec.n) return;
        if (otasFilterAliasSet && !otasFilterAliasSet.has(String(rec.n).toLowerCase())) return;
        const s = rec.s || 'Pending';
        if (osBuckets.hasOwnProperty(s)) osBuckets[s]++;
      });
    });
    map.ota_status = { title: 'OTA Status', layout: 'bignum', items:
      osActiveBuckets.map(k => ({
        label: k,
        value: osBuckets[k],
        tone: k === 'Live' ? 'green' : k === 'Pending' ? 'amber' : 'red',
      })),
    };
    // iter297 — Individual OTA platform cards now count the number of
    // *properties* where that specific platform has a listing link set
    // (i.e. `ota_records[*].u === true`). Status gating still applies
    // when the OTA Status filter is active.
    const otaCols = allOtaCols;
    const statusGate = (otaStatusFilter && otaStatusFilter.length)
      ? new Set(otaStatusFilter)
      : null;
    otaCols.forEach(c => {
      const aliases = aliasOf(c).map(x => x.toLowerCase());
      let cnt = 0;
      rows.forEach(r => {
        const hit = (r.ota_records || []).some(rec => {
          if (!rec || !rec.n) return false;
          if (!aliases.includes(String(rec.n).toLowerCase())) return false;
          if (!rec.u) return false;  // link must be present
          if (statusGate && !statusGate.has(rec.s)) return false;
          return true;
        });
        if (hit) cnt++;
      });
      map[`ota::${c}`] = { title: c, layout: 'single', items: [
        { label: 'Properties', value: cnt, tone: 'teal' },
      ]};
    });
    return map;
  }, [summaryRows, data?.ota_columns, otasFilter, otaStatusFilter]);

  // iter288 — Export to Excel. MUST be declared after `visibleColumns`
  // because the useCallback dependency array references it. Re-issues the
  // same dashboard query the UI used but with `limit=10000` so the
  // download reflects every row that matches the current filters (not
  // just the current page). Columns and their order mirror the on-screen
  // `visibleColumns` so the Excel sheet matches what the user sees.
  const handleExportExcel = useCallback(async () => {
    try {
      setExporting(true);
      const p = new URLSearchParams();
      p.set('date_mode', dateMode);
      if (effectiveRange.start) p.set('start_date', effectiveRange.start);
      if (effectiveRange.end) p.set('end_date', effectiveRange.end);
      if (states.length) p.set('states', states.join('|'));
      if (cities.length) p.set('cities', cities.join('|'));
      if (owners.length) p.set('owners', owners.join('|'));
      if (propertyStatuses.length) p.set('property_statuses', propertyStatuses.join('|'));
      if (docStatuses.length) p.set('doc_statuses', docStatuses.join('|'));
      if (masterFormStatuses.length) p.set('master_form_statuses', masterFormStatuses.join('|'));
      if (propertyNames.length) p.set('property_names', propertyNames.join('|'));
      if (drives.length) p.set('drives', drives.join('|'));
      if (remarksFilter.length) p.set('remarks', remarksFilter.join('|'));
      if (otasFilter.length) p.set('otas', otasFilter.join('|'));
      if (otaStatusFilter.length) p.set('ota_statuses', otaStatusFilter.join('|'));
      if (extraOtas.length) p.set('extra_otas', extraOtas.join('|'));
      if (globalSearch.trim()) p.set('q', globalSearch.trim());
      p.set('page', '1');
      p.set('limit', '10000');
      const r = await fetch(`${API_BASE}/api/reports/dashboard?${p.toString()}`, { headers: authHeader() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const full = await r.json();
      const allRows = full?.data || [];
      if (!allRows.length) {
        toast.message('Nothing to export — current filters returned 0 rows');
        return;
      }
      const sheetRows = allRows.map((r0) => {
        const out = {};
        visibleColumns.forEach((col) => {
          if (col.is_ota) {
            const raw = (r0.ota_status || {})[col.label];
            if (raw && typeof raw === 'object') out[col.label] = raw.status || 'Pending';
            else out[col.label] = raw === 'Live' ? 'Live' : 'Pending';
            return;
          }
          switch (col.key) {
            case 'closed_won_owner': out[col.label] = r0.closed_won_owner || ''; break;
            case 'closed_won_date':  out[col.label] = r0.closed_won_date ? String(r0.closed_won_date).slice(0, 10) : ''; break;
            case 'hotel_name':       out[col.label] = r0.hotel_name || ''; break;
            case 'state':            out[col.label] = r0.state || ''; break;
            case 'city':             out[col.label] = r0.city || ''; break;
            case 'documents_status':
              out[col.label] = r0.documents?.status === 'Complete'
                ? 'Complete'
                : (r0.documents?.pending?.length ? `Pending (${r0.documents.pending.join(', ')})` : 'Pending');
              break;
            case 'master_form_status':
              out[col.label] = r0.master_form?.status || 'Pending';
              break;
            case 'drive':   out[col.label] = r0.drive || ''; break;
            case 'remarks': out[col.label] = r0.remarks || ''; break;
            default:        out[col.label] = '';
          }
        });
        return out;
      });
      const ws = XLSX.utils.json_to_sheet(sheetRows, { header: visibleColumns.map(c => c.label) });
      ws['!cols'] = visibleColumns.map((c) => {
        const headerLen = (c.label || '').length;
        const maxVal = sheetRows.reduce((m, row) => Math.max(m, String(row[c.label] ?? '').length), 0);
        return { wch: Math.min(40, Math.max(10, Math.max(headerLen, maxVal) + 2)) };
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Reports Dashboard');
      const ts = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
      XLSX.writeFile(wb, `reports_dashboard_${ts}.xlsx`);
      toast.success(`Exported ${sheetRows.length} row${sheetRows.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  }, [dateMode, effectiveRange, states, cities, owners, propertyStatuses, docStatuses,
      masterFormStatuses, propertyNames, drives, remarksFilter, otasFilter, otaStatusFilter,
      extraOtas, globalSearch, visibleColumns]);

  const handleRemarkSaved = (leadId, newRemark) => {
    setData(prev => prev ? {
      ...prev,
      data: prev.data.map(r => r.id === leadId ? { ...r, manual_remark: newRemark } : r),
    } : prev);
  };

  // Optimistic update after a user manually sets Masterform to
  // Correction / Complete / Pending from the popover. Backend may
  // auto-clear the lock on a non-Complete transition.
  const handleMasterFormStatusSaved = (leadId, newStatus, newLocked) => {
    setData(prev => prev ? {
      ...prev,
      data: prev.data.map(r => r.id === leadId
        ? { ...r, master_form: {
            ...(r.master_form || {}),
            status: newStatus,
            locked: typeof newLocked === 'boolean' ? newLocked : (r.master_form?.locked || false),
          } }
        : r),
    } : prev);
  };

  // Admin-only lock toggle result.
  const handleMasterFormLockChanged = (leadId, newLocked) => {
    setData(prev => prev ? {
      ...prev,
      data: prev.data.map(r => r.id === leadId
        ? { ...r, master_form: { ...(r.master_form || {}), locked: newLocked } }
        : r),
    } : prev);
  };

  const openDrive = (link) => {
    if (!link) return;
    const url = /^https?:\/\//i.test(link) ? link : `https://${link}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Cell renderer keyed by column.
  const renderCell = (col, r) => {
    if (col.is_ota) {
      // iter285 — backend now returns {status, listing_url, hotel_link}. Keep
      // backward compatibility by coercing legacy string values ("Live"/"N/A").
      const raw = (r.ota_status || {})[col.label];
      const cell = (raw && typeof raw === 'object')
        ? raw
        : { status: raw === 'Live' ? 'Live' : 'Pending', listing_url: '', hotel_link: '' };
      const openLink = (url) => {
        if (!url) return;
        const u = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        window.open(u, '_blank', 'noopener,noreferrer');
      };
      const testid = `dashboard-ota-${col.label}-${r.id}`;
      let body;
      if (cell.status === 'Live') {
        body = cell.listing_url ? (
          <button onClick={() => openLink(cell.listing_url)} className="focus:outline-none" title="Open OTA listing" data-testid={`${testid}-live-link`}>
            <Badge tone="green" testid={testid}>Live</Badge>
          </button>
        ) : <Badge tone="green" testid={testid}>Live</Badge>;
      } else if (cell.status === 'Integrated') {
        body = cell.hotel_link ? (
          <button onClick={() => openLink(cell.hotel_link)} className="focus:outline-none" title="Open hotel link" data-testid={`${testid}-integrated-link`}>
            <Badge tone="green" testid={testid}>Integrated</Badge>
          </button>
        ) : <Badge tone="green" testid={testid}>Integrated</Badge>;
      } else if (cell.status === 'Self Manage') {
        body = <Badge tone="red" testid={testid}>Self Manage</Badge>;
      } else if (cell.status === 'Integration') {
        body = <Badge tone="red" testid={testid}>Integration</Badge>;
      } else {
        body = <Badge tone="amber" testid={testid}>Pending</Badge>;
      }
      return <td key={col.key} className="px-3 py-2 whitespace-nowrap">{body}</td>;
    }
    switch (col.key) {
      case 'closed_won_owner':
        return <td key={col.key} className="px-3 py-2 whitespace-nowrap text-gray-700">{r.closed_won_owner || '—'}</td>;
      case 'closed_won_date':
        return <td key={col.key} className="px-3 py-2 whitespace-nowrap text-gray-500">{fmtDate(r.closed_won_date)}</td>;
      case 'hotel_name':
        return <td key={col.key} className="px-3 py-2 whitespace-nowrap font-medium text-gray-800">{r.hotel_name || '—'}</td>;
      case 'state':
        return <td key={col.key} className="px-3 py-2 whitespace-nowrap text-gray-600">{r.state || '—'}</td>;
      case 'city':
        return <td key={col.key} className="px-3 py-2 whitespace-nowrap text-gray-600">{r.city || '—'}</td>;
      case 'documents_status':
        return (
          <td key={col.key} className="px-3 py-2 whitespace-nowrap">
            {r.documents.status === 'Complete' ? (
              <Badge tone="green" testid={`dashboard-docs-${r.id}`}><Check size={11}/> Complete</Badge>
            ) : (
              <button onClick={() => setPendingModal({ hotel: r.hotel_name, items: r.documents.pending })}
                className="focus:outline-none" data-testid={`dashboard-docs-pending-${r.id}`}>
                <Badge tone="red">Pending</Badge>
              </button>
            )}
          </td>
        );
      case 'master_form_status':
        return (
          <td key={col.key} className="px-3 py-2 whitespace-nowrap">
            <MasterFormCell
              row={r}
              onSaved={handleMasterFormStatusSaved}
              onLockChanged={handleMasterFormLockChanged}
              isAdmin={isAdmin}
            />
          </td>
        );
      case 'drive':
        return (
          <td key={col.key} className="px-3 py-2 whitespace-nowrap">
            {r.drive === 'Done' && r.drive_link ? (
              <button onClick={() => openDrive(r.drive_link)} className="focus:outline-none" data-testid={`dashboard-drive-done-${r.id}`} title="Open Drive link">
                <Badge tone="green">Done</Badge>
              </button>
            ) : (
              <Badge tone="red" testid={`dashboard-drive-pending-${r.id}`}>Pending</Badge>
            )}
          </td>
        );
      case 'remarks':
        return (
          <td key={col.key} className="px-3 py-2 whitespace-nowrap">
            <RemarkEditor row={r} onSaved={handleRemarkSaved}/>
          </td>
        );
      default:
        return <td key={col.key} className="px-3 py-2 whitespace-nowrap text-gray-400">—</td>;
    }
  };

  return (
    <div className="space-y-4" data-testid="reports-dashboard-tab">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <select value={dateMode} onChange={(e) => setDateMode(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs bg-white" data-testid="dashboard-date-mode">
            <option value="all">All</option>
            <option value="custom">Custom</option>
            <option value="week">Week</option>
            <option value="monthly">Monthly</option>
          </select>
          {dateMode === 'custom' && (
            <>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs" data-testid="dashboard-start-date"/>
              <span className="text-xs text-gray-400">to</span>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs" data-testid="dashboard-end-date"/>
            </>
          )}
          {dateMode === 'monthly' && (
            <input type="month" value={monthPick} onChange={(e) => setMonthPick(e.target.value)}
              className="px-2 py-1.5 border border-gray-300 rounded-lg text-xs" data-testid="dashboard-month"/>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-2 py-1 bg-white border border-gray-300 rounded-lg">
            <Search size={12} className="text-gray-400"/>
            <input
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              placeholder="Search property..."
              className="bg-transparent outline-none text-xs w-48"
              data-testid="dashboard-global-search"
            />
            {globalSearch && (
              <button onClick={() => setGlobalSearch('')} className="text-gray-400 hover:text-red-500" data-testid="dashboard-global-search-clear">
                <X size={11}/>
              </button>
            )}
          </div>
          <button onClick={resetFilters} className="text-xs text-teal-600 hover:underline" data-testid="dashboard-reset-filters">Reset filters</button>
          {/* iter288 — Export the current filtered dashboard as .xlsx. */}
          <button
            onClick={handleExportExcel}
            disabled={exporting || loading}
            className="px-2 py-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded hover:bg-emerald-100 disabled:opacity-50 flex items-center gap-1"
            title="Export currently filtered rows to Excel"
            data-testid="dashboard-export-excel"
          >
            {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </button>
          {isAdmin && (
            <button onClick={() => setManageOpen(true)}
              className="px-2 py-1.5 text-xs text-gray-600 hover:text-teal-600 hover:bg-gray-100 rounded flex items-center gap-1"
              title="Manage Headers"
              data-testid="dashboard-manage-headers-open">
              <Settings2 size={13}/> Manage Headers
            </button>
          )}
          <button onClick={fetchData} className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-gray-100 rounded" title="Refresh" data-testid="dashboard-refresh"><RefreshCw size={14}/></button>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-gray-200">
        <SearchableMultiSelect label="State" options={data?.filters?.states || []} value={states}
          onChange={setStates} testid="dashboard-filter-state"/>
        <SearchableMultiSelect label="City" options={data?.filters?.cities || []} value={cities}
          onChange={setCities} testid="dashboard-filter-city"/>
        <SearchableMultiSelect label="Closed Won Owner" options={data?.filters?.owners || []} value={owners}
          onChange={setOwners} testid="dashboard-filter-owner"/>
        <SearchableMultiSelect label="Property Name" options={data?.filters?.hotel_names || []} value={propertyNames}
          onChange={setPropertyNames} testid="dashboard-filter-property-name"/>
        <SearchableMultiSelect label="Property Status" options={data?.filters?.property_statuses || []} value={propertyStatuses}
          onChange={setPropertyStatuses} testid="dashboard-filter-prop-status"/>
        <SearchableMultiSelect label="Document Status" options={['Complete', 'Pending']} value={docStatuses}
          onChange={setDocStatuses} testid="dashboard-filter-doc-status"/>
        <SearchableMultiSelect label="Master Form" options={['Complete', 'Correction', 'Pending']} value={masterFormStatuses}
          onChange={setMasterFormStatuses} testid="dashboard-filter-mform-status"/>
        <SearchableMultiSelect label="Drive" options={data?.filters?.drives || ['Done', 'Pending']} value={drives}
          onChange={setDrives} testid="dashboard-filter-drive"/>
        <SearchableMultiSelect label="Remarks" options={data?.filters?.remarks || ['Active', 'Inactive']} value={remarksFilter}
          onChange={setRemarksFilter} testid="dashboard-filter-remarks"/>
        {/* iter285 — OTA & OTA Status filters. OTA options come from the
            dashboard default OTA list; status options are the five canonical
            labels surfaced by the Dashboard renderer. */}
        <SearchableMultiSelect label="OTA"
          options={data?.filters?.otas || otaOptions.default_columns || []}
          value={otasFilter} onChange={setOtasFilter}
          testid="dashboard-filter-ota"/>
        <SearchableMultiSelect label="OTA Status"
          options={data?.filters?.ota_statuses || ['Live', 'Integrated', 'Self Manage', 'Integration', 'Pending']}
          value={otaStatusFilter} onChange={setOtaStatusFilter}
          testid="dashboard-filter-ota-status"/>

        <div className="relative">
          <button onClick={() => setAddOtaOpen(o => !o)}
            className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs flex items-center gap-1 hover:bg-teal-700" data-testid="dashboard-add-ota">
            <Plus size={12}/> Add OTA
          </button>
          {addOtaOpen && (
            <div className="absolute z-30 right-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
              <div className="px-2 py-1 border-b border-gray-100 text-[11px] font-bold text-gray-500 uppercase">Add OTA column</div>
              {availableOtaAdds.length === 0 && <div className="p-2 text-xs text-gray-400">No more OTAs available</div>}
              {availableOtaAdds.map(n => (
                <button key={n} onClick={() => addOta(n)} className="w-full text-left px-2 py-1.5 text-xs hover:bg-teal-50" data-testid={`dashboard-add-ota-${n}`}>{n}</button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* iter292/293 — Dashboard Summary Cards (reference-matched visual).
          Sits between the filter bar and the data table. Counts come from
          `summaryRows` which uses the exact same filter payload as the
          table (just a larger `limit`), so cards and table are always
          perfectly in sync. */}
      <div className="my-6" data-testid="dashboard-summary-cards">
        {summaryLoading && !summaryRows.length ? (
          <div className="text-xs text-gray-400 py-4">Loading summary…</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {(cardConfig || []).filter(c => c.enabled).map((cfg) => {
              // iter295 — OTA cards: when the OTA filter is active, show
              // only cards for the selected platforms. All other filters
              // left untouched.
              if (cfg.key.startsWith('ota::') && otasFilter.length) {
                const platform = cfg.key.slice(5);
                if (!otasFilter.includes(platform)) return null;
              }
              const spec = cardData[cfg.key];
              if (!spec) return null;
              // iter294 — Dynamic category visibility. Status-driven cards
              // (`bignum` + `list`) hide any item whose count is 0 so the
              // card reflects only the categories present in the current
              // filtered dataset. `single`-layout cards (All Properties,
              // per-OTA platform) always render their lone number — a
              // legitimate zero becomes the empty state.
              const rawItems = spec.items || [];
              const items = spec.layout === 'single' ? rawItems : rawItems.filter(it => it.value > 0);
              // OTA cards pick a stable palette slot by their position in
              // the dashboard OTA column list (so colors don't shuffle
              // when the user reorders cards in Manage Headers).
              let otaIdx = 0;
              if (cfg.key.startsWith('ota::')) {
                const otaCols = data?.ota_columns || [];
                otaIdx = Math.max(0, otaCols.indexOf(cfg.key.slice(5)));
              }
              const { cardBg, iconBg, iconColor, Icon } = getCardStyle(cfg.key, otaIdx);
              return (
                <div
                  key={cfg.key}
                  className={`${cardBg} rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow border border-white/60 flex flex-col`}
                  data-testid={`dashboard-summary-card-${cfg.key}`}
                >
                  {/* Header: icon + title (+ optional subtitle) */}
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className={`${iconBg} ${iconColor} w-8 h-8 rounded-lg flex items-center justify-center shrink-0 shadow-sm`}>
                      <Icon size={16} strokeWidth={2.25} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-800 truncate">{spec.title}</div>
                      {spec.subtitle && <div className="text-[10px] text-gray-500 -mt-0.5">{spec.subtitle}</div>}
                    </div>
                  </div>

                  {/* Body — layout per card type */}
                  {items.length === 0 ? (
                    <div className="flex-1 flex flex-col justify-center">
                      <div className="text-3xl font-bold tabular-nums text-gray-300">0</div>
                      <div className="text-xs text-gray-400 mt-0.5">No matching records</div>
                    </div>
                  ) : spec.layout === 'single' ? (
                    <div className="flex-1 flex flex-col justify-center">
                      <div className={`text-3xl font-bold tabular-nums ${toneToTextClass(items[0].tone)}`}>{items[0].value}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{items[0].label}</div>
                    </div>
                  ) : spec.layout === 'bignum' ? (
                    <div className={`grid grid-cols-${Math.min(items.length, 4)} gap-3`}>
                      {items.map(it => (
                        <div key={it.label} className="flex flex-col">
                          <span className={`text-2xl font-bold tabular-nums ${toneToTextClass(it.tone)}`}>{it.value}</span>
                          <span className="text-[11px] text-gray-500 mt-0.5 truncate" title={it.label}>{it.label}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // list layout — Property Status / Account / Remarks.
                    // Two columns; dot + label on left, count right-aligned.
                    <div className="grid grid-cols-2 gap-x-5 gap-y-1.5">
                      {items.map(it => (
                        <div key={it.label} className="flex items-center justify-between min-w-0" title={it.label}>
                          <span className="flex items-center gap-1.5 min-w-0">
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                              it.tone === 'green' ? 'bg-emerald-500'
                              : it.tone === 'amber' ? 'bg-amber-500'
                              : it.tone === 'red'   ? 'bg-red-500'
                              : it.tone === 'teal'  ? 'bg-teal-500'
                              : 'bg-gray-400'
                            }`} />
                            <span className="text-xs text-gray-700 truncate">{it.label}</span>
                          </span>
                          <span className="text-sm font-semibold text-gray-800 tabular-nums shrink-0 pl-2">{it.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                {visibleColumns.map(col => (
                  <th key={col.key} className="px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap border-b border-gray-200" data-testid={`dashboard-col-header-${col.key}`}>
                    <span className="flex items-center gap-1">
                      {col.label}
                      {col.is_ota && extraOtas.includes(col.label) && (
                        <button onClick={() => removeOtaColumn(col.label)} className="text-gray-400 hover:text-red-500" title="Remove column"><X size={11}/></button>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={visibleColumns.length || 1} className="px-3 py-10 text-center text-gray-400"><Loader2 className="animate-spin inline mr-2" size={14}/> Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={visibleColumns.length || 1} className="px-3 py-10 text-center text-gray-400">No accounts match the current filters.</td></tr>
              )}
              {!loading && rows.map(r => (
                <tr key={r.id} className="odd:bg-white even:bg-gray-50 border-t border-gray-100" data-testid={`dashboard-row-${r.id}`}>
                  {visibleColumns.map(col => renderCell(col, r))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>Showing {(data.page - 1) * data.limit + 1}–{Math.min(data.page * data.limit, data.total)} of {data.total} properties</span>
          <div className="flex items-center gap-1">
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
              className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50" data-testid="dashboard-prev">Prev</button>
            <span className="px-2">Page {data.page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50" data-testid="dashboard-next">Next</button>
          </div>
        </div>
      )}

      {/* Pending documents modal */}
      {pendingModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPendingModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="dashboard-pending-modal">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-bold text-gray-800 text-sm">Pending Documents</h3>
                <p className="text-xs text-gray-500 mt-0.5">{pendingModal.hotel}</p>
              </div>
              <button onClick={() => setPendingModal(null)} className="text-gray-400 hover:text-gray-600"><X size={16}/></button>
            </div>
            <ul className="space-y-1.5">
              {pendingModal.items.map(item => (
                <li key={item} className="flex items-center gap-2 text-sm text-gray-700"><X size={12} className="text-red-500"/> {item}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* iter322 — Manage Headers modal (admin only) */}
      <ManageHeadersModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        columns={mergedColumns}
        onSaved={(cols) => setColumnsConfig(cols)}
        cards={cardConfig}
        onCardsSaved={saveCardConfig}
        onCardsReset={resetCardConfig}
      />
    </div>
  );
};

export default ReportsDashboard;
